import React, { useContext, useState, memo, useEffect } from "react";

import { RemixBuilderContext } from "./RemixBuilderContext";
import TemplatesModal from "../../templates/TemplatesModal";
import TemplatePreview from "../../templates/TemplatePreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useRemix from "./useRemix";

const UploadLinkTag = memo(() => {
  const [contextData] = useContext(RemixBuilderContext);

  return (
    <span
      onClick={() => {
        window.open(contextData.uploaded_image_url, "_blank");
      }}
      className="bg-white py-[3px] px-[11px] rounded-xl gap-1 flex items-center text-xl w-fit max-w-[200px] cursor-pointer">
      <svg className="w-[35px]" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.47272 7.23809L2.98375 8.72705C2.42766 9.28314 2.10787 10.0398 2.11372 10.835C2.11956 11.6302 2.43235 12.3915 3.01482 12.956C3.57928 13.5384 4.3407 13.8512 5.13578 13.8571C5.949 13.863 6.68773 13.5613 7.24385 13.0052L8.73282 11.5162M10.6347 9.6507L12.1237 8.16173C12.6798 7.60564 12.9995 6.849 12.9937 6.05379C12.9879 5.25858 12.6751 4.49732 12.0926 3.93283C11.5283 3.36851 10.767 3.0557 9.97177 3.04986C9.17656 3.04402 8.41982 3.34565 7.8637 3.90176L6.37473 5.39072M5.29578 10.6625L9.76269 6.19559" stroke="#7F7D83" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"></path>
      </svg>
      <span className="text-ellipsis overflow-hidden whitespace-nowrap text-slate-500">
        {contextData.uploaded_image_url}
      </span>
    </span>
  );
});

const PreviewStepOne = memo(({ sortedSelectedSections }) => {
  return (
    <>
      <div className="remix-preview-step-one mt-5 p-7 rounded-xl bg-slate-200 overflow-y-auto flex-1">
        <div className="flex flex-column flex-1 grow-1 items-center gap-3 justify-center self-start">
          <UploadLinkTag />

          {sortedSelectedSections.map((section, index) => (
            <div className="section-uploaded-image-preview border-solid border-2 border-slate-300 overflow-hidden flex justify-center items-center" key={index}>
              <img className="w-full h-full" src={`data:image/png;base64,${section.image_bytes}`} alt={`Section ${index}`} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

const PreviewStepTwo = memo(({ sortedMatchedSections, remixSettings, selectedSection, onSelectSection, onOpenTemplatesModal, onFeedback, getSectionFeedbackStatus, stepPositions, activeStep, onStepChange }) => {
  const [contextData, builder] = useContext(RemixBuilderContext);
  const fromScratch = contextData.from_scratch === true;
  const canAddFunnelStep = builder.canAddFunnelStep ? builder.canAddFunnelStep() : false;

  const changeToNextMatchedSection = (section) => {
    const currentIndex = section.currentTemplateIndex || 0;
    const hasMoreTemplates = section.matchedTemplates && currentIndex + 1 < section.matchedTemplates.length;

    if (hasMoreTemplates) {
      builder.tryNextTemplate(section);
    } else {
      onSelectSection(section);
      onOpenTemplatesModal();
    }
  };

  return (
    <>
      <div className="remix-preview-step-two mt-5 p-7 rounded-xl bg-slate-200 overflow-y-auto flex-1 flex flex-col">
        {!fromScratch && (
          <div className="flex flex-col md:flex-row gap-5 mb-5">
            <div className="w-full md:w-1/4 text-center flex flex-wrap justify-center gap-2">
              {contextData.uploaded_image_url && (
                <React.Fragment>
                  <p className="font-medium m-0 text-xl">Uploaded sections</p>
                  <UploadLinkTag />
                </React.Fragment>
              )}
            </div>
            <div className="w-full md:w-3/4 text-center">
              <p className="font-medium m-0 text-xl">Layout Preview:</p>
            </div>
          </div>
        )}

        {fromScratch && (
          <p className="font-medium m-0 text-xl mb-5 text-center">Layout Preview</p>
        )}
        {stepPositions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {stepPositions.map((stepPosition) => (
              <button
                key={`preview-step-${stepPosition}`}
                type="button"
                className={`px-4 py-2 rounded-full text-xl border ${
                  stepPosition === activeStep
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-solid border-slate-300"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onStepChange(stepPosition);
                }}
              >
                {builder.getFunnelStepName(stepPosition)}
              </button>
            ))}
            <button
              type="button"
              disabled={!canAddFunnelStep}
              className={`px-4 py-2 rounded-full text-xl border border-dashed bg-transparent ${
                canAddFunnelStep
                  ? "border-slate-400 text-gray-700"
                  : "border-slate-300 text-slate-400 cursor-not-allowed opacity-60"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (!canAddFunnelStep) return;
                builder.addFunnelStep();
              }}
            >
              + Add Step
            </button>
          </div>
        )}

        {sortedMatchedSections.length === 0 ? (
          <div className="w-full">
            <div className="bg-slate-200 border border-slate-300 rounded-xl flex items-center justify-center min-h-[200px] mt-5">
              <span className="text-slate-500 text-xl">No sections added. Add a section template to the right.</span>
            </div>
          </div>
        ) : (
          sortedMatchedSections.map((section, index) => (
            <div
              key={`section-row-${section.id}`}
              className={`flex flex-col gap-2 rounded-xl cursor-pointer ${
                fromScratch ? "w-full" : "mb-4 last:mb-0 md:flex-row"
              }`}
              onClick={() => onSelectSection(section)}
            >
              {/* Left side - Uploaded section thumbnail (hidden when from_scratch) */}
              {!fromScratch && (
                <div className="w-full md:w-1/4 flex flex-col gap-3 items-center">
                  <div className="section-uploaded-template-preview w-full flex gap-3 text-xl">
                    <span className="flex-shrink-0 w-6 text-center">{index + 1}</span>
                    <div
                      className={`overflow-hidden flex justify-center items-center rounded-xl w-full ${
                        selectedSection?.id === section.id ? "border border-solid border-blue-600" : "border-0"
                      }`}
                    >
                      {section.image_bytes && (
                        <img
                          className="w-full h-full object-cover"
                          src={`data:image/png;base64,${section.image_bytes}`}
                          alt={`Section ${index}`}
                        />
                      )}

                      {!section.image_bytes && section.screenshot && (
                        <img
                          className="w-full h-full object-cover"
                          src={section.screenshot}
                          alt={`Section ${index}`}
                        />
                      )}

                      {!section.image_bytes && !section.screenshot && (
                        <div className="flex justify-center items-center">
                          <p className="m-0 p-2 text-slate-500 text-xl">No screenshot available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Template preview - full width when from_scratch, 3/4 when not */}
              <div className={fromScratch ? "w-full" : "w-full md:w-3/4"}>
                <div
                  data-section-id={section.id}
                  className={`remix-preview-section w-full ${
                    selectedSection?.id === section.id ? "border border-solid border-blue-600" : "border-0"
                  }`}
                >
                  <TemplatePreview
                    key={`section-uploaded-preview-${section?.template?.id}`}
                    template={section?.template}
                    options={remixSettings}
                  >
                    <div className="thumbnail-overlay absolute inset-0 m-[10px] gap-1 flex flex-col text-xl justify-between">
                      {contextData.current_user?.super_admin === true &&
                        section.tags && (
                          <div className="flex flex-wrap gap-1 justify-end">
                            {section.tags?.map((tag, index) => (
                              <span
                                key={`section-tag-${tag?.id}`}
                                className="bg-white py-[1px] px-[8px] border-solid border-1 border-slate-300 rounded-xl flex items-center cursor-pointer"
                              >
                                {tag.name}
                              </span>
                            ))}
                        </div>
                      )}

                      <div className="flex justify-end gap-2">
                        {section.matchedTemplates && section.matchedTemplates.length > 1 && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              changeToNextMatchedSection(section);
                            }}
                            style={{ display: "none" }}
                            className="thumbnail-button"
                          >
                            <div className="ai-button text-xl">
                              <svg
                                width="19"
                                height="19"
                                viewBox="0 0 19 19"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M11.6184 2.30005L12.9911 6.00971L16.7008 7.3824L12.9911 8.7551L11.6184 12.4648L10.2457 8.7551L6.53608 7.3824L10.2457 6.00971L11.6184 2.30005Z"
                                  stroke="white"
                                  strokeWidth="1.3"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M5.26549 10.7706L6.46461 12.5362L8.23019 13.7353L6.46461 14.9345L5.26549 16.7L4.06637 14.9345L2.30078 13.7353L4.06637 12.5362L5.26549 10.7706Z"
                                  stroke="white"
                                  strokeWidth="1.3"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              Try Again
                            </div>
                          </span>
                        )}

                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectSection(section);
                            onOpenTemplatesModal();
                          }}
                          style={{ display: "none" }}
                          className="thumbnail-button"
                        >
                          <div className="bg-white py-[7px] px-[20px] border-solid border-1 border-slate-200 rounded-xl flex gap-1 items-center cursor-pointer text-xl">
                            <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6.59844 1.8999C6.59844 1.54092 6.30742 1.2499 5.94844 1.2499C5.58945 1.2499 5.29844 1.54092 5.29844 1.8999H6.59844ZM5.29844 13.0999C5.29844 13.4589 5.58945 13.7499 5.94844 13.7499C6.30742 13.7499 6.59844 13.4589 6.59844 13.0999H5.29844ZM3.49844 2.5499H10.4984V1.2499H3.49844V2.5499ZM11.9484 3.9999V10.9999H13.2484V3.9999H11.9484ZM10.4984 12.4499H3.49844V13.7499H10.4984V12.4499ZM2.04844 10.9999V3.9999H0.748438V10.9999H2.04844ZM3.49844 12.4499C2.69762 12.4499 2.04844 11.8007 2.04844 10.9999H0.748438C0.748438 12.5187 1.97965 13.7499 3.49844 13.7499V12.4499ZM11.9484 10.9999C11.9484 11.8007 11.2992 12.4499 10.4984 12.4499V13.7499C12.0172 13.7499 13.2484 12.5187 13.2484 10.9999H11.9484ZM10.4984 2.5499C11.2992 2.5499 11.9484 3.19909 11.9484 3.9999H13.2484C13.2484 2.48112 12.0172 1.2499 10.4984 1.2499V2.5499ZM3.49844 1.2499C1.97965 1.2499 0.748438 2.48112 0.748438 3.9999H2.04844C2.04844 3.19909 2.69762 2.5499 3.49844 2.5499V1.2499ZM5.29844 1.8999V13.0999H6.59844V1.8999H5.29844ZM5.94844 8.1499H12.2484V6.8499H5.94844V8.1499Z" fill="black"/>
                            </svg>
                            Change Layout
                          </div>
                        </span>
                      </div>
                    </div>
                  </TemplatePreview>
                </div>

                {section.remixMatched && (
                  <div className="flex flex-row justify-end gap-2 mt-2">
                    {(() => {
                      const feedbackStatus = getSectionFeedbackStatus(section);
                      const hasVoted = !!feedbackStatus;
                      const likedThis = feedbackStatus === 'like';
                      const dislikedThis = feedbackStatus === 'dislike';

                      return (
                        <>
                          {/* Like button */}
                          <div
                            className={`pointer-events-auto group/like-dislike cursor-pointer ${hasVoted ? 'hidden' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!hasVoted) {
                                onFeedback(section, 'like');
                              }
                            }}
                          >
                            <div className={`text-slate-500 group-hover/like-dislike:hidden`}>
                              <FontAwesomeIcon icon="fa-regular fa-thumbs-up" />
                            </div>

                            <div className={`text-slate-500 invisible group-hover/like-dislike:visible group-hover/like-dislike:text-black`}>
                              <FontAwesomeIcon icon="fa-solid fa-thumbs-up" />
                            </div>
                          </div>

                          {/* Dislike button */}
                          <div
                            className={`pointer-events-auto group/like-dislike cursor-pointer ${hasVoted ? 'hidden' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!hasVoted) {
                                onFeedback(section, 'dislike');
                                changeToNextMatchedSection(section);
                              }
                            }}
                          >
                            <div className={`text-slate-500 group-hover/like-dislike:hidden`}>
                              <FontAwesomeIcon icon="fa-regular fa-thumbs-down" />
                            </div>

                            <div className={`text-slate-500 invisible group-hover/like-dislike:visible group-hover/like-dislike:text-black`}>
                              <FontAwesomeIcon icon="fa-solid fa-thumbs-down" />
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          ))
        )}

        <div className="h-[80px]" />
      </div>
    </>
  );
});

const RemixBuilderPreview = () => {
  const [contextData, builder] = useContext(RemixBuilderContext);
  const { Remix } = useRemix({ builder: true });

  // Use same position sort as Customize template layout so preview order matches bullet list
  const positionSort = (a, b) => (a.position || 0) - (b.position || 0)

  const sortedSelectedSections = (contextData.uploaded_sections || [])
    .filter((section) => section.selected)
    .slice()
    .sort(positionSort)

  const sectionStepPositions = Array.from(
    new Set((contextData.matched_sections || []).map((section) => section.step_position || 1))
  );
  const namedStepPositions = Object.keys(contextData.funnel_step_names || {})
    .map((stepPosition) => parseInt(stepPosition, 10))
    .filter((stepPosition) => Number.isFinite(stepPosition) && stepPosition > 0);
  const activeStep =
    contextData.active_funnel_step ||
    sectionStepPositions[0] ||
    namedStepPositions[0] ||
    1;
  const stepPositions = Array.from(
    new Set([...sectionStepPositions, ...namedStepPositions, activeStep])
  ).sort((a, b) => a - b);

  const sortedMatchedSections = (contextData.matched_sections || [])
    .filter((section) => section.selected)
    .filter((section) => (section.step_position || 1) === activeStep)
    .slice()
    .sort(positionSort)

  const [selectedSection, setSelectedSection] = useState(null);
  const [remixTemplatesModalOpen, setRemixTemplatesModalOpen] = useState(false);
  const [remixTemplatesModalInitialFilters, setRemixTemplatesModalInitialFilters] = useState({});
  const [sectionFeedbacks, setSectionFeedbacks] = useState({});

  const changeSelectedSection = (section) => {
    setSelectedSection(section);
    if (section?.step_position) {
      builder.setActiveFunnelStep(section.step_position);
    }
    const transformedTags = Remix.transformSectionTagsForModal(section.tags);
    setRemixTemplatesModalInitialFilters(transformedTags);

    const matched_sections_layout = document.querySelector('#remix-matched-sections-templates');
    if (matched_sections_layout) {
      matched_sections_layout.querySelectorAll('.matched-section-template').forEach((sectionDiv) => {
        sectionDiv.classList.remove('selected');

        if (sectionDiv.getAttribute('data-section-id') === section.id) {
          sectionDiv.classList.add('selected');
        }
      });
    }
  }

  const handleFeedback = (section, feedbackType) => {
    const key = `${section.id}-${section?.template?.id}`;

    // Prevent double feedback
    if (sectionFeedbacks[key]) {
      return;
    }

    setSectionFeedbacks(prev => ({
      ...prev,
      [key]: feedbackType
    }));
  }

  const getSectionFeedbackStatus = (section) => {
    const key = `${section.id}-${section?.template?.id}`;
    return sectionFeedbacks[key];
  }

  useEffect(() => {
    if (sortedMatchedSections.length > 0 && !selectedSection) {
      changeSelectedSection(sortedMatchedSections[0]);
    }
  }, [sortedMatchedSections]);

  const showPreview =
    contextData.uploaded_sections?.length > 0 ||
    (contextData.from_scratch && contextData.matched_sections?.length > 0) ||
    (contextData.from_scratch && contextData.current_step >= 2);

  return (
    <React.Fragment>
      {showPreview && (
        <div className="remix-editor-preview flex flex-column overflow-scroll grow-1 min-w-0">
          {contextData.current_step === 1 && (
            <PreviewStepOne
              sortedSelectedSections={sortedSelectedSections}
            />
          )}
          {(contextData.current_step > 1) && (
            <>
              <PreviewStepTwo
                sortedMatchedSections={sortedMatchedSections}
                remixSettings={contextData.remix_settings}
                selectedSection={selectedSection}
                onSelectSection={changeSelectedSection}
                onOpenTemplatesModal={() => setRemixTemplatesModalOpen(true)}
                onFeedback={handleFeedback}
                getSectionFeedbackStatus={getSectionFeedbackStatus}
                stepPositions={stepPositions}
                activeStep={activeStep}
                onStepChange={(stepPosition) => {
                  builder.setActiveFunnelStep(stepPosition);
                  const nextSection = (contextData.matched_sections || [])
                    .filter((section) => section.selected)
                    .filter((section) => (section.step_position || 1) === stepPosition)
                    .slice()
                    .sort(positionSort)[0];
                  setSelectedSection(nextSection || null);
                }}
              />
              <TemplatesModal
                website={contextData.website}
                isOpen={remixTemplatesModalOpen}
                onClose={() => setRemixTemplatesModalOpen(false)}
                templates={contextData.section_templates}
                selectedSection={selectedSection}
                initialFilters={remixTemplatesModalInitialFilters}
                onSelectTemplate={(sectionTemplate) => {
                  selectedSection.remixMatched = false;
                  selectedSection.currentTemplateIndex = 0;
                  builder.changeMatchedSectionTemplate(selectedSection, sectionTemplate, true);
                }}
              />
            </>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default memo(RemixBuilderPreview);
